import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { SharedHeader } from "@/components/SharedHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { 
  Trophy, Star, Medal, Crown, ArrowLeft, User, Gamepad2
} from "lucide-react";

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const gameFilters = ["all", "Spades", "Whist", "Backgammon", "Checkers", "Chess", "Dominoes"];

  useEffect(() => {
    fetchLeaderboard();
  }, [filter]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?game_type=${filter}` : "";
      const response = await axios.get(`${API}/ratings/leaderboard${params}`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (err) {
      console.log("Could not fetch leaderboard");
      setLeaderboard([]);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center font-bebas text-lg text-[#A1A1AA]">{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return "bg-gradient-to-r from-yellow-500/20 to-transparent border-yellow-500/30";
    if (rank === 2) return "bg-gradient-to-r from-gray-400/20 to-transparent border-gray-400/30";
    if (rank === 3) return "bg-gradient-to-r from-amber-600/20 to-transparent border-amber-600/30";
    return "bg-[#0A0A0A] border-white/10";
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    const stars = [];
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} className="w-4 h-4 text-[#7a6200] fill-[#7a6200]" />);
      } else if (i === fullStars && hasHalf) {
        stars.push(<Star key={i} className="w-4 h-4 text-[#7a6200] fill-[#7a6200]/50" />);
      } else {
        stars.push(<Star key={i} className="w-4 h-4 text-[#52525B]" />);
      }
    }
    return stars;
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="leaderboard-page">
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-ubuntu text-sm">Back to Home</span>
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#7a6200]/20 flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-[#7a6200]" />
            </div>
            <h1 className="font-bebas text-4xl tracking-wider text-white mb-2">
              PLAYER LEADERBOARD
            </h1>
            <p className="text-[#A1A1AA] font-ubuntu">
              Top-rated players in the Board2Boston™ community
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {gameFilters.map((game) => (
              <button
                key={game}
                onClick={() => setFilter(game)}
                className={`px-4 py-2 rounded-full text-sm font-ubuntu capitalize transition-colors ${
                  filter === game
                    ? "bg-[#7a6200] text-black"
                    : "bg-white/5 text-[#A1A1AA] border border-white/10 hover:border-[#7a6200]/50"
                }`}
              >
                {game === "all" ? "All Games" : game}
              </button>
            ))}
          </div>

          {/* Leaderboard */}
          {loading ? (
            <div className="text-center py-12 text-[#A1A1AA]">Loading leaderboard...</div>
          ) : leaderboard.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="py-12 text-center">
                <Trophy className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                <p className="text-[#A1A1AA] font-ubuntu">No rankings yet.</p>
                <p className="text-[#52525B] text-sm font-ubuntu mt-2">
                  Attend events and receive ratings to appear on the leaderboard!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((player, index) => (
                <Card 
                  key={player.user_id}
                  className={`${getRankBg(player.rank)} border hover:border-[#7a6200]/50 transition-colors`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Rank */}
                      <div className="flex-shrink-0 w-10 flex justify-center">
                        {getRankIcon(player.rank)}
                      </div>
                      
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {player.avatar ? (
                          <img 
                            src={player.avatar} 
                            alt={player.full_name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-[#7a6200]/50"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center border-2 border-[#7a6200]/50">
                            <span className="text-[#7a6200] font-bebas text-xl">
                              {player.full_name?.charAt(0) || "?"}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Player Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bebas text-lg text-white truncate">
                          {player.full_name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          <span className="text-[#52525B] capitalize">{player.skill_level}</span>
                          {player.gaming_preferences?.slice(0, 3).map((game) => (
                            <span 
                              key={game}
                              className="px-2 py-0.5 rounded bg-white/5 text-[#A1A1AA] text-xs"
                            >
                              {game}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Rating */}
                      <div className="flex-shrink-0 text-right">
                        <div className="flex items-center gap-1 justify-end mb-1">
                          {renderStars(player.average_rating)}
                        </div>
                        <p className="font-bebas text-xl text-[#7a6200]">
                          {player.average_rating.toFixed(1)}
                        </p>
                        <p className="text-[#52525B] text-xs font-ubuntu">
                          {player.total_ratings} rating{player.total_ratings !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Call to Action */}
          <div className="mt-8 text-center">
            <p className="text-[#A1A1AA] font-ubuntu text-sm mb-4">
              Want to climb the rankings? Attend events and earn positive ratings!
            </p>
            <Button
              onClick={() => navigate("/home", { state: { section: "find-games" }})}
              className="btn-gold"
            >
              <Gamepad2 className="w-4 h-4 mr-2" />
              Find Events
            </Button>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10 bg-[#0A0A0A] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#52525B] font-ubuntu text-sm">
            © 2026 All Rights Reserved | Board2Boston™ | 713 Consulting and Development
          </p>
          <p className="text-[#52525B] font-ubuntu text-xs mt-2">
            1942 W. Gray St., Box #1259 | Houston, TX 77019 | United States
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LeaderboardPage;
